import React, { useEffect, useRef, useState } from 'react';
import { useFontCache } from '../hooks/useFontCache';

// ============================================================
// Medição do chassi dentro do modal (comprimento + altura, em cm)
// Réplica da lógica do protótipo HTML, adaptada para React.
// Componente "só leitura": recebe a fonte/texto/tamanho/espaçamento
// que já existem no modal e apenas EXIBE a medição. Não toca em
// impressão, saldo nem na lista.
// ============================================================

// Padrão CSS: 96px = 1in = 25,4mm
const PX_PARA_CM = 2.54 / 96;
const PX_POR_CM = 1 / PX_PARA_CM;
const PX_POR_MM = PX_POR_CM / 10;
const COR_REGUA = '#1f2937'; // gray-800 (preto suave)

// Fatores de calibração da impressora — COMPRIMENTO e ALTURA são separados,
// porque a impressora pode esticar/comprimir um pouquinho diferente em cada eixo.
// A conversão px→cm assume 96 dpi; estes fatores corrigem para a impressora real.
// Para reajustar no futuro: imprima um teste, meça com régua e calcule, por eixo:
//   FATOR = medida_real_em_cm / medida_mostrada_em_cm (sem o fator, ou seja a 96 dpi)
// Calibrado em 01/06/2026 (fonte "argentino"):
//   comprimento: 7,46 (régua) / 7,65 (96 dpi)  = 0,9752
//   altura:      0,715 (régua) / 0,769 (96 dpi) = 0,9297
const FATOR_CALIBRACAO_COMPRIMENTO = 0.9752;
const FATOR_CALIBRACAO_ALTURA = 0.9297;

// BRASIL — réplica EXATA do que a impressão usa (Matricial.tsx / Termica.tsx):
// fonte "BRASIL", 20px, espaçamento 5px, negrito. E o deslocamento vertical fixo:
// na impressão o chassi fica em padding-top 34px e o BRASIL em 71px, ou seja o
// BRASIL começa 37px ABAIXO do topo do chassi (geometria fixa da chapa).
const BRASIL_FONT_FAMILY = 'BRASIL';
const BRASIL_TAMANHO = 20;
const BRASIL_ESPACAMENTO = 5;
const OFFSET_BRASIL_PX = 71 - 34; // 37px

interface Props {
  /** Nome da família da fonte (arquivo sem ".ttf"), igual ao usado no input do modal */
  fontFamily: string;
  /** Texto do chassi a medir */
  texto: string;
  /** Tamanho da fonte em px */
  tamanho: number;
  /** Espaçamento entre letras em px */
  espacamento: number;
  /** Se deve incluir a palavra "BRASIL" na medição (igual ao checkbox do modal) */
  incluirBrasil?: boolean;
}

// Mede a "tinta" real do texto (contorno desenhado, sem folgas laterais)
// via canvas — mesma técnica do HTML.
function medirTinta(
  texto: string,
  fontFamily: string,
  tamanho: number,
  espacamento: number
): { wPx: number; hPx: number } | null {
  if (!texto) return { wPx: 0, hPx: 0 };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.font = `bold ${tamanho}px "${fontFamily}", monospace`;

  let usouLetterSpacing = false;
  const ctxAny = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in ctx) {
    try {
      ctxAny.letterSpacing = `${espacamento}px`;
      usouLetterSpacing = true;
    } catch {
      /* navegador sem suporte a letterSpacing no canvas */
    }
  }

  const m = ctx.measureText(texto);
  let wInk = (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || 0);
  const hInk = (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0);

  // Se o canvas não aplicou o espaçamento, soma manualmente.
  if (!usouLetterSpacing && espacamento) {
    wInk += espacamento * Math.max(0, texto.length - 1);
  }

  return { wPx: wInk, hPx: hInk };
}

// Régua horizontal (marcas mm / 5mm / cm + rótulos em cm)
function ReguaH({ widthPx }: { widthPx: number }) {
  if (widthPx <= 0) {
    return <svg width={0} height={0} className="block" style={{ justifySelf: 'center' }} />;
  }

  const altura = 22;
  const widthCm = widthPx * PX_PARA_CM;
  const totalMm = Math.floor(widthCm * 10);

  const marcas: React.ReactNode[] = [];
  for (let i = 0; i <= totalMm; i++) {
    const x = i * PX_POR_MM;
    const h = i % 10 === 0 ? 12 : i % 5 === 0 ? 8 : 4;
    marcas.push(
      <line
        key={`t${i}`}
        x1={x}
        x2={x}
        y1={altura - h}
        y2={altura}
        stroke={COR_REGUA}
        strokeWidth={1}
      />
    );
    if (i % 10 === 0) {
      marcas.push(
        <text
          key={`l${i}`}
          x={x}
          y={altura - 14}
          textAnchor={i === 0 ? 'start' : 'middle'}
          fontSize={9}
          fontFamily="system-ui, sans-serif"
          fill={COR_REGUA}
        >
          {i / 10}
        </text>
      );
    }
  }

  return (
    <svg
      width={widthPx}
      height={altura}
      viewBox={`0 0 ${widthPx} ${altura}`}
      className="block"
      style={{ justifySelf: 'center' }}
    >
      <line x1={0} x2={widthPx} y1={altura - 0.5} y2={altura - 0.5} stroke={COR_REGUA} strokeWidth={1} />
      {marcas}
      {/* Marca final exata na ponta direita */}
      <line x1={widthPx - 0.5} x2={widthPx - 0.5} y1={altura - 14} y2={altura} stroke={COR_REGUA} strokeWidth={1} />
    </svg>
  );
}

// Régua vertical (marcas mm / 5mm / cm + rótulos em cm)
function ReguaV({ heightPx }: { heightPx: number }) {
  if (heightPx <= 0) {
    return <svg width={0} height={0} className="block" style={{ alignSelf: 'center' }} />;
  }

  const largura = 30;
  const heightCm = heightPx * PX_PARA_CM;
  const totalMm = Math.floor(heightCm * 10);

  const marcas: React.ReactNode[] = [];
  for (let i = 0; i <= totalMm; i++) {
    const y = i * PX_POR_MM;
    const w = i % 10 === 0 ? 12 : i % 5 === 0 ? 8 : 4;
    marcas.push(
      <line
        key={`t${i}`}
        x1={largura - w}
        x2={largura}
        y1={y}
        y2={y}
        stroke={COR_REGUA}
        strokeWidth={1}
      />
    );
    if (i % 10 === 0) {
      marcas.push(
        <text
          key={`l${i}`}
          x={largura - 14}
          y={y}
          textAnchor="end"
          dominantBaseline={i === 0 ? 'hanging' : 'middle'}
          fontSize={9}
          fontFamily="system-ui, sans-serif"
          fill={COR_REGUA}
        >
          {i / 10}
        </text>
      );
    }
  }

  return (
    <svg
      width={largura}
      height={heightPx}
      viewBox={`0 0 ${largura} ${heightPx}`}
      className="block"
      style={{ alignSelf: 'center' }}
    >
      <line x1={largura - 0.5} x2={largura - 0.5} y1={0} y2={heightPx} stroke={COR_REGUA} strokeWidth={1} />
      {marcas}
      {/* Marca final exata na base */}
      <line x1={largura - 14} x2={largura} y1={heightPx - 0.5} y2={heightPx - 0.5} stroke={COR_REGUA} strokeWidth={1} />
    </svg>
  );
}

export function MedidorImpressao({ fontFamily, texto, tamanho, espacamento, incluirBrasil = false }: Props) {
  const medidorRef = useRef<HTMLDivElement>(null);
  const { loadFontFromCache } = useFontCache();
  const [wPx, setWPx] = useState(0);
  const [hPx, setHPx] = useState(0);

  const textoMedir = (texto || '').toUpperCase();

  useEffect(() => {
    let cancelado = false;

    const medir = () => {
      if (cancelado) return;

      if (!textoMedir || !fontFamily) {
        setWPx(0);
        setHPx(0);
        return;
      }

      // Tinta real via canvas
      const t = medirTinta(textoMedir, fontFamily, tamanho, espacamento);

      // Fallback: caixa CSS via elemento oculto (só se o canvas falhar)
      let boxW = 0;
      let boxH = 0;
      if (medidorRef.current) {
        const el = medidorRef.current;
        el.textContent = textoMedir;
        el.style.fontFamily = `"${fontFamily}", monospace`;
        el.style.fontSize = `${tamanho}px`;
        el.style.letterSpacing = `${espacamento}px`;
        el.style.fontWeight = 'bold';
        el.style.textTransform = 'uppercase';
        const r = el.getBoundingClientRect();
        boxW = r.width;
        boxH = r.height;
      }

      const chassiW = t && t.wPx > 0 ? t.wPx : boxW;
      const chassiH = t && t.hPx > 0 ? t.hPx : boxH;

      // Altura: com "Incluir BRASIL" ligado, a altura impressa vai do topo do
      // chassi até a base do BRASIL. Na impressão o BRASIL fica num ponto fixo
      // (37px abaixo do topo do chassi), com fonte própria. Então a altura total
      // é a maior entre: o chassi sozinho, ou o vão de 37px somado à altura da
      // palavra BRASIL. (Por isso, em tamanhos normais de chassi, a altura passa
      // a depender principalmente da posição fixa do BRASIL.)
      let alturaFinal = chassiH;
      if (incluirBrasil) {
        const b = medirTinta('BRASIL', BRASIL_FONT_FAMILY, BRASIL_TAMANHO, BRASIL_ESPACAMENTO);
        const brasilH = b && b.hPx > 0 ? b.hPx : 0;
        alturaFinal = Math.max(chassiH, OFFSET_BRASIL_PX + brasilH);
      }

      // Comprimento continua sendo o do chassi (o BRASIL é mais estreito e não
      // muda a largura útil; por isso o BRASIL entra só na altura).
      setWPx(chassiW);
      setHPx(alturaFinal);
    };

    // Garante a fonte BRASIL carregada antes de medir (mesma fonte da impressão)
    if (incluirBrasil) {
      loadFontFromCache('brasil.ttf').then(() => {
        if (!cancelado) medir();
      });
    }

    // Mede agora e novamente quando as fontes terminarem de carregar
    medir();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(medir, 0);
      });
    }

    return () => {
      cancelado = true;
    };
  }, [textoMedir, fontFamily, tamanho, espacamento, incluirBrasil, loadFontFromCache]);

  const wCm = (wPx * PX_PARA_CM * FATOR_CALIBRACAO_COMPRIMENTO).toFixed(2).replace('.', ',');
  const hCm = (hPx * PX_PARA_CM * FATOR_CALIBRACAO_ALTURA).toFixed(2).replace('.', ',');

  return (
    <div className="mt-3">
      {/* Pré-visualização com réguas */}
      <div className="p-2 rounded-md bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
        <div className="text-xs text-gray-600 mb-2">Medição:</div>
        <div className="bg-white rounded p-2 overflow-x-auto">
          <div
            style={{
              display: 'inline-grid',
              gridTemplateColumns: 'auto auto',
              gridTemplateRows: 'auto auto',
              columnGap: 10,
              rowGap: 8,
            }}
          >
            <div />
            <ReguaH widthPx={wPx} />
            <ReguaV heightPx={hPx} />
            <div style={{ position: 'relative', height: hPx, minWidth: wPx }}>
              <div
                className="whitespace-nowrap text-gray-900"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  lineHeight: 1,
                  fontFamily: `"${fontFamily}", monospace`,
                  fontSize: `${tamanho}px`,
                  letterSpacing: `${espacamento}px`,
                  // Sem negrito de propósito: esta prévia espelha o campo de
                  // digitação (também sem negrito), para não confundir o usuário.
                  // A MEDIÇÃO continua em negrito, como a impressão real
                  // (ver medirTinta e o medidor oculto abaixo).
                  fontWeight: 'normal',
                  textTransform: 'uppercase',
                }}
              >
                {textoMedir}
              </div>
              {incluirBrasil && (
                <div
                  className="whitespace-nowrap text-gray-900"
                  style={{
                    position: 'absolute',
                    top: OFFSET_BRASIL_PX,
                    left: 0,
                    width: wPx,
                    textAlign: 'center',
                    lineHeight: 1,
                    fontFamily: `"${BRASIL_FONT_FAMILY}", monospace`,
                    fontSize: `${BRASIL_TAMANHO}px`,
                    letterSpacing: `${BRASIL_ESPACAMENTO}px`,
                    fontWeight: 'normal',
                    textTransform: 'uppercase',
                  }}
                >
                  BRASIL
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Painel de medidas */}
      <div className="mt-3 p-3 rounded-md bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 text-sm">
        <div className="flex justify-between items-baseline">
          <span className="text-gray-700">Comprimento estimado:</span>
          <span>
            <span className="text-xl font-bold text-blue-700">{wCm}</span>
            <span className="text-base text-blue-600 ml-1">cm</span>
          </span>
        </div>
        <div className="flex justify-between items-baseline mt-1">
          <span className="text-gray-700">Altura estimada:</span>
          <span>
            <span className="text-xl font-bold text-blue-700">{hCm}</span>
            <span className="text-base text-blue-600 ml-1">cm</span>
          </span>
        </div>
      </div>

      {/* Aviso sobre a escala de impressão do navegador */}
      <p className="mt-3 text-sm text-gray-600 leading-relaxed">
        Para um correto funcionamento da estimativa de comprimento, certifique-se de estar utilizando a escala de impressão padrão ou 100% do navegador. Essa escala fica disponível no momento de escolha da impressora na próxima página de impressão.
      </p>

      {/* Medidor oculto (fallback de medição) */}
      <div
        ref={medidorRef}
        aria-hidden="true"
        style={{ position: 'absolute', left: -99999, top: 0, visibility: 'hidden', whiteSpace: 'nowrap' }}
      />
    </div>
  );
}

export default MedidorImpressao;
